// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Checkbox from 'ui/atoms/Checkbox'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import BaseField from 'ui/molecules/BaseField'
import DateRangeField from 'ui/molecules/DateRangeField'
import TimeRangeField from 'ui/molecules/TimeRangeField'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'

function AdvancedDateSettings(): React.Node {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)

  const question = useBuilderQuestion(selectedItem?.id)

  const questionSubtype =
    typeof question?.question_type === 'object'
      ? question?.question_type?.question_subtype
      : ''

  const isTimeOnly = questionSubtype === 'time_only'
  const isMonthYear = questionSubtype === 'month_year'
  const isDateTime = questionSubtype === 'datetime'

  const onChangeDateRange = React.useCallback(
    (dateMin, dateMax) => {
      setQuestion(selectedItem.id, {
        ...question,
        date_min: dateMin,
        date_max: dateMax,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const onChangeDateFutureOnly = React.useCallback(
    (dateFutureOnly) => {
      setQuestion(selectedItem.id, {
        ...question,
        date_future_only: dateFutureOnly,
        date_past_only: false,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const onChangeDatePastOnly = React.useCallback(
    (datePastOnly) => {
      setQuestion(selectedItem.id, {
        ...question,
        date_past_only: datePastOnly,
        date_future_only: false,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const pickerType = React.useMemo(() => {
    if (isMonthYear) {
      return 'month'
    }
    return null
  }, [isMonthYear])

  return (
    <Space>
      <BaseField semibold label="Past & Future">
        <Space size="xsmall">
          <Flex align="center" className="mt-1">
            <Checkbox
              checked={Boolean(question?.date_past_only)}
              className="mr-2"
              key="date_past_only"
              onChange={(e) => {
                onChangeDatePastOnly(e.target.checked)
              }}
            />
            <Text $small>Past Only</Text>
          </Flex>
          <Flex align="center">
            <Checkbox
              checked={Boolean(question?.date_future_only)}
              className="mr-2"
              key="date_future_only"
              onChange={(e) => {
                onChangeDateFutureOnly(e.target.checked)
              }}
            />
            <Text $small>Future Only</Text>
          </Flex>
          <Text $xlight $xsmall classname="italic">
            Allow to select {isTimeOnly ? 'times' : 'dates'} in the past or
            future
          </Text>
        </Space>
      </BaseField>

      <BaseField semibold label="Start & End Date">
        <Space className="pt-1" size="xsmall">
          {!isTimeOnly ? (
            <DateRangeField
              onChange={(value) => {
                if (value && value.length === 2) {
                  onChangeDateRange(value[0][0], value[0][1])
                } else {
                  onChangeDateRange(null, null)
                }
              }}
              picker={pickerType}
              showFutureOnly={question?.date_future_only}
              showPastOnly={question?.date_past_only}
              showTime={isDateTime}
              value={
                question?.date_min && question?.date_max
                  ? [question?.date_min, question?.date_max]
                  : undefined
              }
            />
          ) : (
            <TimeRangeField
              onChange={(value) => {
                if (value && value.length === 2) {
                  onChangeDateRange(value[0][0], value[0][1])
                } else {
                  onChangeDateRange(null, null)
                }
              }}
              showFutureOnly={question?.date_future_only}
              showPastOnly={question?.date_past_only}
              value={
                question?.date_min && question?.date_max
                  ? [question?.date_min, question?.date_max]
                  : undefined
              }
            />
          )}
          <Text $xlight $xsmall classname="italic">
            Allow to select only between these {isTimeOnly ? 'times' : 'dates'}
          </Text>
        </Space>
      </BaseField>
    </Space>
  )
}

export default AdvancedDateSettings
